import { Box, Container, Flex, Image } from "@theme-ui/components"
import React, { useState } from "react"
import { useMenu } from "../hooks/useMenu"
import { getHomePath } from "../utils/path"
import { InboundLink } from "./link"
import { MagicLink } from "../utils/magicLink"
import { debounce } from "lodash"
import { useMotionValueEvent, useScroll } from "framer-motion"
import { LanguageSwitcherContext } from "../context/languageSwitcherContext"
import Logo from "../assets/images/logo.inline.svg"
import LogoMecmatica from "../assets/images/logo-mecmatica-primary.svg"
import { i18nContext } from "../context/i18nContext"
import SidebarNav from "./sidebarNav"
import LanguageSwitcher from "./languageSwitcher"

const Nav = () => {
  const locale = React.useContext(LanguageSwitcherContext).activeLocale
  const menu = useMenu()
  const [showSideDrawer, setShowSideDrawer] = useState(false)
  const [headerTheme, setHeaderTheme] = useState(false)
  const { scrollY } = useScroll()
  useMotionValueEvent(scrollY, "change", latest => {
    latest > 100 ? setHeaderTheme(true) : setHeaderTheme(false)
  })

  const primaryMenu = menu && menu[0] ? menu[0].treeChildren : []
  const secondaryMenu = menu && menu[1] ? menu[1].treeChildren : []

  primaryMenu &&
    primaryMenu.map(menuItem => {
      menuItem.treeChildren.sort((a, b) => a.position - b.position)
      menuItem.treeChildren.map(menuItem => {
        if (menuItem.treeChildren && menuItem.treeChildren.length > 0) {
          menuItem.treeChildren.sort((a, b) => a.position - b.position)
        }
      })
    })

  const sideDrawerClosedHandler = () => {
    setShowSideDrawer(false)

    if (typeof window != "undefined" && window.document) {
      document.body.style.overflow = "unset"
    }
  }

  return (
    <i18nContext.Consumer>
      {t => (
        <>
          <Box
            as="nav"
            sx={{
              position: "fixed",
              width: "100%",
              transition: ".2s all ease-in-out",
              backgroundColor: headerTheme ? "white" : "transparent",
              color: headerTheme ? "white" : "inherit",
              boxShadow: headerTheme
                ? "0px 10px 15px -3px rgba(0,0,0,0.1);"
                : "none",
            }}
          >
            <Container
              variant="fw"
              sx={{
                transition: ".2s all ease-in-out",
                paddingY: headerTheme ? [2, 2, 2] : null,
                borderBottom: "1px solid",
                borderColor: "lighten",
              }}
            >
              <Flex
                sx={{ flexDirection: "row", justifyContent: "space-between" }}
              >
                {headerTheme ? (
                  <Box
                    sx={{
                      paddingY: 3,
                    }}
                  >
                    <InboundLink to={getHomePath(t.locale)}>
                      <Image src={LogoMecmatica} alt="" />
                    </InboundLink>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      paddingY: 3,
                      svg: {
                        height: ["40px"],
                        width: "auto",
                        path: { fill: "light" },
                      },
                    }}
                  >
                    <InboundLink to={getHomePath(t.locale)}>
                      <Logo />
                    </InboundLink>
                  </Box>
                )}
                <Flex
                  sx={{
                    flexDirection: "row",
                    padding: 0,
                    margin: 0,
                    listStyle: "none",
                  }}
                  as="ul"
                >
                  {primaryMenu.map(item => (
                    <TextComponent
                      item={item}
                      locale={locale}
                      key={item.id}
                      theme={headerTheme}
                    />
                  ))}
                  <Flex
                    sx={{
                      alignItems: "center",
                      px: [3],
                      a: {
                        // color: headerTheme ? "primary" : "light",
                        textDecoration: "none",
                      },
                    }}
                  >
                    <LanguageSwitcher theme={headerTheme} />
                  </Flex>
                  <Flex
                    sx={{
                      alignItems: "center",
                      color: "light",
                    }}
                  >
                    <Flex
                      sx={{ pl: [3], alignItems: "center", cursor: "pointer" }}
                      onClick={() => setShowSideDrawer(!showSideDrawer)}
                    >
                      <Hamburger show={showSideDrawer} theme={headerTheme} />
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </Container>
          </Box>
          <SidebarNav
            open={showSideDrawer}
            sideBarData={secondaryMenu}
            closed={sideDrawerClosedHandler}
          />
        </>
      )}
    </i18nContext.Consumer>
  )
}

const TextComponent = ({ item, locale, theme }) => {
  const [show, setShow] = useState(false)

  const debouncedHandleMouseEnterMenu = debounce(() => {
    setShow(true)
  }, 200)

  const handlOnMouseLeaveMenu = () => {
    setShow(false)
    debouncedHandleMouseEnterMenu.cancel()
  }

  return (
    <Box
      as="li"
      key={item.id}
      sx={{
        display: "flex",
        alignItems: "center",
        position: "relative",
        marginX: 1,
        paddingX: 3,
        paddingY: 3,
        color: "light",
        a: {
          color: theme ? "primary" : "light",
          fontFamily: "montserrat",
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      }}
      onMouseEnter={() => debouncedHandleMouseEnterMenu()}
      onMouseLeave={() => handlOnMouseLeaveMenu()}
    >
      {item.link ? (
        <MagicLink item={item.link} locale={locale}></MagicLink>
      ) : (
        <Box
          sx={{
            cursor: "default",
            fontFamily: "montserrat",
            color: theme ? "primary" : "light",
          }}
        >
          {item.anchor}
        </Box>
      )}
      {item.treeChildren.length > 0 && show && (
        <Box
          as="ul"
          sx={{
            listStyle: "none",
            padding: 3,
            margin: 0,
            backgroundColor: "lightBackground",
            position: "absolute",
            top: 50,
            width: "max-content",
            boxShadow:
              "0 4px 6px rgb(50 50 93 / 11%), 0 1px 3px rgb(0 0 0 / 8%)",
            borderRadius: "sm",
          }}
        >
          {item.treeChildren.map(item =>
            item.anchor ? (
              <Box
                as="li"
                key={item.id}
                sx={{
                  color: "primary",
                  py: [1],
                  a: {
                    fontSize: [2],
                    fontWeight: "normal",
                    fontFamily: "montserrat",
                    color: "primary",
                    textDecoration: "none",
                    transition: ".2s all ease",
                    "&:hover": {
                      color: "dark",
                      textDecoration: "none",
                    },
                  },
                }}
              >
                {item.link ? (
                  <MagicLink item={item.link} locale={locale} />
                ) : (
                  <Box sx={{ cursor: "default" }}>{item.anchor} </Box>
                )}
              </Box>
            ) : null
          )}
        </Box>
      )}
    </Box>
  )
}

export default Nav

const Hamburger = ({ show, theme }) => {
  return (
    <Box
      className={show ? "show" : ""}
      sx={{
        width: "25px",
        "&:before,&:after,& div": {
          borderRadius: "sm",
          content: "''",
          backgroundColor: theme ? "primary" : "light",
          display: "block",
          height: "3px",
          my: [1],
          transition: ".55s",
        },
        "&.show:before": {
          transform: "translateY(8px) rotate(135deg)",
        },
        "&.show:after": {
          transform: "translateY(-6px) rotate(-135deg)",
        },
        "&.show div": {
          transform: "scale(0)",
        },
      }}
    >
      <Box></Box>
    </Box>
  )
}
